import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Select, Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useRoleOptions } from '../../hooks/useRoleOptions.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { type BulkResponse, BulkResultDialog } from './BulkResultDialog.js';
import { BAN_NOTE_MAX, BAN_REASON_LABEL } from './banReason.js';
import { CUSTOMER, StaffRoleSelect } from './roleCard.js';

export interface BulkUser {
  _id: string;
  email: string;
}

interface BulkActionBarProps {
  selectedIds: Set<string>;
  /** The currently listed users — used to label result rows with emails. */
  users: BulkUser[];
  onClear: () => void;
}

const STATUS_VALUES = ['active', 'inactive', 'banned'] as const;

// The three bulk actions and ONLY these — impersonation is explicitly never
// offered as a bulk action (FR-014).
type BulkAction = 'status' | 'password-reset' | 'role';

/** 037: one bulk action applied to the whole selection, with an explicit
 * confirm step naming the action, target value, and affected count (FR-003),
 * and a per-row result dialog afterwards (FR-011). */
export function BulkActionBar({ selectedIds, users, onClear }: BulkActionBarProps) {
  const ability = useAbility();
  const queryClient = useQueryClient();
  const [action, setAction] = useState<BulkAction>('status');
  const [status, setStatus] = useState<(typeof STATUS_VALUES)[number]>('active');
  // 376: the staff role to set ('' = none, customer only); each account's Customer is kept.
  const [role, setRole] = useState<string>('');
  // 334 (FR-003) and 367 (design 1f): every role, greyed where it cannot be given here.
  const { roleOptions } = useRoleOptions();
  const [confirming, setConfirming] = useState(false);
  // 376 (#1262): Add / Remove customer, confirmed like the other actions; each account's staff role is kept.
  const [customerChange, setCustomerChange] = useState<'add' | 'remove' | null>(null);
  const [result, setResult] = useState<BulkResponse | null>(null);
  // 381 (#1271, design 5): a bulk ban asks one reason for all (Accident, or Other with a note).
  const [banReason, setBanReason] = useState<'accident' | 'other' | null>(null);
  const [banNote, setBanNote] = useState('');
  const banNoteId = useId();

  const mutation = useMutation({
    mutationFn: () => {
      const user_ids = [...selectedIds];
      if (customerChange) {
        return api.post('/users/bulk/customer', {
          user_ids,
          customer: customerChange === 'add',
        }) as Promise<BulkResponse>;
      }
      if (action === 'status') {
        return api.post('/users/bulk/status', {
          user_ids,
          status,
          ...(status === 'banned'
            ? { ban_reason: banReason, ...(banNote.trim() ? { note: banNote.trim() } : {}) }
            : {}),
        }) as Promise<BulkResponse>;
      }
      if (action === 'role') {
        return api.post('/users/bulk/role', {
          user_ids,
          role: role || CUSTOMER,
        }) as Promise<BulkResponse>;
      }

      return api.post('/users/bulk/password-reset', { user_ids }) as Promise<BulkResponse>;
    },
    onSuccess: (data) => {
      setConfirming(false);
      setCustomerChange(null);
      setBanReason(null);
      setBanNote('');
      setResult(data);
    },
  });

  if (!ability.can('manage', 'User') || selectedIds.size === 0) return null;

  const count = selectedIds.size;
  const banning = !customerChange && action === 'status' && status === 'banned';
  const banValid =
    banReason !== null &&
    banNote.trim().length <= BAN_NOTE_MAX &&
    (banReason !== 'other' || banNote.trim().length > 0);
  const actionLabel = customerChange
    ? customerChange === 'add'
      ? 'Add Customer (staff role kept)'
      : 'Remove Customer (staff role kept)'
    : action === 'status'
      ? `Change status to "${status}"`
      : action === 'role'
        ? role
          ? `Set staff role to "${role}" (Customer kept)`
          : 'Remove the staff role (customer only)'
        : 'Send password reset';

  return (
    <div
      data-testid="bulk-action-bar"
      className="mb-3 flex flex-wrap items-center gap-3 rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline bg-surface px-4 py-2 text-sm"
    >
      <span className="font-medium text-ink">{count} selected</span>
      <Button size="sm" variant="secondary" onClick={onClear}>
        Clear
      </Button>

      <Select
        aria-label="Bulk action"
        className="ml-2"
        value={action}
        onChange={(e) => setAction(e.target.value as BulkAction)}
      >
        <option value="status">Change status</option>
        <option value="password-reset">Send password reset</option>
        <option value="role">Set staff role</option>
      </Select>

      {action === 'status' && (
        <Select
          aria-label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as (typeof STATUS_VALUES)[number])}
        >
          {STATUS_VALUES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
      )}

      {action === 'role' && (
        <>
          <StaffRoleSelect
            id="bulk_role"
            aria-label="New staff role"
            value={role}
            options={roleOptions}
            bulk
            onChange={(e) => setRole(e.target.value)}
          />
          <span className="text-xs text-steel">Customer is kept on each account.</span>
        </>
      )}

      <Button
        size="sm"
        onClick={() => {
          setCustomerChange(null);
          setConfirming(true);
        }}
      >
        Apply
      </Button>
      <span className="ml-auto flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setCustomerChange('add');
            setConfirming(true);
          }}
        >
          Add customer
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setCustomerChange('remove');
            setConfirming(true);
          }}
        >
          Remove customer
        </Button>
      </span>

      <Modal open={confirming} onClose={() => setConfirming(false)} title="Confirm bulk action">
        <div className="px-6 py-4 space-y-4 text-sm text-ink">
          <p>
            {actionLabel} for{' '}
            <strong>
              {count} user{count === 1 ? '' : 's'}
            </strong>
            ?
          </p>
          {banning && (
            <fieldset className="flex flex-col gap-2" data-testid="bulk-ban-reason">
              <legend className="mb-1 font-medium">Reason (one for all)</legend>
              <p className="text-xs text-steel">
                "{BAN_REASON_LABEL.unpaid_penalty}" is set one account at a time, from the user's
                page.
              </p>
              {(['accident', 'other'] as const).map((r) => (
                <label
                  key={r}
                  className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${
                    banReason === r ? 'border-primary bg-primary/5' : 'border-hairline'
                  }`}
                >
                  <input
                    type="radio"
                    name="bulk-ban-reason"
                    value={r}
                    checked={banReason === r}
                    onChange={() => setBanReason(r)}
                  />
                  <span>
                    {BAN_REASON_LABEL[r]}
                    {r === 'other' && (
                      <span className="block text-xs text-muted">A note is required</span>
                    )}
                  </span>
                </label>
              ))}
              <label htmlFor={banNoteId} className="flex flex-col gap-1">
                <span className="font-medium">
                  Note{' '}
                  <span className="font-normal text-muted">
                    {banReason === 'other' ? '(required)' : '(optional)'}
                  </span>
                </span>
                <Textarea
                  id={banNoteId}
                  aria-label="Ban note"
                  rows={2}
                  maxLength={BAN_NOTE_MAX}
                  value={banNote}
                  onChange={(e) => setBanNote(e.target.value)}
                />
              </label>
            </fieldset>
          )}
          {mutation.isError && (
            <p className="text-red-600">
              {(mutation.error as { message?: string }).message ?? 'Bulk action failed'}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant={banning ? 'danger' : 'primary'}
              disabled={banning && !banValid}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      <BulkResultDialog
        result={result}
        users={users}
        onClose={() => {
          setResult(null);
          queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.user.list });
          onClear();
        }}
      />
    </div>
  );
}
